import { Injectable } from '@angular/core';
import { QuickLink } from '../shared/models/quick-links';
import { ApphostService } from '../shared/services/app-host.service';
import { LoggingService } from '../shared/services/logging.service';
import {
  TessEntitlement,
  TessEntitlementConfig,
  VoucherEntitlement,
  VoucherEntitlementConfig,
} from '../shared/models/enums';

@Injectable({
  providedIn: 'root',
})
export class LandingService {
  constructor(
    private apphostService: ApphostService,
    private loggingService: LoggingService,
  ) {}

  createQuickLinksFromEntitlements(entitlements: string[]): QuickLink[] {
    if (!this.apphostService.userInfo || !entitlements.length) {
      return [];
    }

    const targetEntitlements = this.getTargetEntitlements(entitlements);
    if (!targetEntitlements.length) {
      return [];
    }

    return this.buildQuickLinks(targetEntitlements);
  }

  getDashboardTitle(entitlements: string[]): string {
    return this.isValidEnrollmentEntitlement(entitlements)
      ? 'My Exams'
      : this.isValidVouchersEntitlement(entitlements)
        ? 'Vouchers'
        : '';
  }

  private getTargetEntitlements(entitlements: string[]): string[] {
    return this.isValidVouchersEntitlement(entitlements)
      ? [VoucherEntitlement.NASDAPRIV_VOUCHER]
      : this.isValidEnrollmentEntitlement(entitlements)
        ? entitlements.filter((ent) => Object.values(TessEntitlement).includes(ent as TessEntitlement))
        : [];
  }

  private buildQuickLinks(targetEntitlements: string[]): QuickLink[] {
    const result: QuickLink[] = [];

    targetEntitlements.forEach((entitlement, index) => {
      const quickLinkConfig =
        TessEntitlementConfig[entitlement as TessEntitlement] ||
        VoucherEntitlementConfig[entitlement as VoucherEntitlement];

      if (quickLinkConfig) {
        const linkUrl = this.buildLinkUrl(entitlement, quickLinkConfig);
        if (linkUrl) {
          result.push({
            id: `entitlement-${index}`,
            label: quickLinkConfig.label || this.formatEntitlementLabel(entitlement),
            url: '#',
            icon: 'fa-certificate',
            target: '_blank',
            pendoMapping: quickLinkConfig.urlSuffix.toLowerCase() + 'LegacyLink',
            onClick: () => this.handleQuickLinkClick(linkUrl),
          });
        }
      }
    });

    return result;
  }

  private buildLinkUrl(entitlement: string, quickLinkConfig: any): string {
    const baseUrl =
      this.isValidVouchersEntitlement([entitlement]) && VoucherEntitlementConfig[entitlement as VoucherEntitlement]
        ? this.apphostService.appConfig.voucher_url
        : this.isValidEnrollmentEntitlement([entitlement]) && TessEntitlementConfig[entitlement as TessEntitlement]
          ? this.apphostService.appConfig.enrollment_url
          : '';

    return baseUrl && baseUrl.includes('vouchers') ? baseUrl : `${baseUrl}/${quickLinkConfig.urlSuffix}`;
  }

  private formatEntitlementLabel(entitlement: string): string {
    return entitlement.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  private handleQuickLinkClick(url: string): void {
    const urlWithParam = `${url}${url.includes('?') ? '&' : '?'}FINPROGW_AUTH=true`;
    this.loggingService.log(`Quick link clicked: ${urlWithParam}`);
    window.location.href = urlWithParam;
  }

  private isValidEnrollmentEntitlement(entitlements: string[]): boolean {
    const url = window.location.href;
    return (
      url.includes('/tess/exams') &&
      entitlements.filter((ent) => Object.values(TessEntitlement).includes(ent as TessEntitlement)).length > 0
    );
  }

  private isValidVouchersEntitlement(entitlements: string[]): boolean {
    const url = window.location.href;
    return url.includes('/vouchers') && entitlements?.includes(VoucherEntitlement.NASDAPRIV_VOUCHER);
  }
}
